// eslint-disable-next-line no-unused-vars
import { EventFormatScoringBreakdownGroup } from "common/helpers/eventFormatHelper";
// eslint-disable-next-line no-unused-vars
import { EventFormatScoringBreakdownRecord } from "common/helpers/eventFormatHelper";
import ModalHelper from "common/helpers/modalHelper";
import { rankLabel, tierFor } from "common/helpers/scoreHelper";
import * as React from "react";
import { ModalCloseButton } from "../CustomModal/Modal/ModalFrame";
import "./ScorePillDetails.scss";

/**
 * @param {EventFormatScoringBreakdownRecord} record
 */
const maxPointsFor = (record) =>
	Number(record.pointsPer || 0) * Number(record.maxCount || 0);

/**
 * @typedef ScorePillDetailsProps
 * @type {object}
 * @property {EventFormatScoringBreakdownGroup[]} groups
 */

/**
 * @param {ScorePillDetailsProps} props
 * @param {any} bracketScore
 */
export default function ScorePillDetails({ groups, bracketScore }) {
	const scoreGroups = groups ?? [];
	// the format builder previews a format with no user attached
	const isPreview = bracketScore == null;
	const scoreBreakdown = bracketScore?.data ? JSON.parse(bracketScore.data) : [];
	const rank = bracketScore?.rank ?? 1;
	const percent = bracketScore?.percentile ?? 100;
	const totalScore = bracketScore?.totalScore ?? 0;
	const maxPoints = scoreGroups.reduce(
		(sum, group) =>
			sum + group.records.reduce((s, record) => s + maxPointsFor(record), 0),
		0
	);

	const league = tierFor(rank, percent);
	const hasTier = !isPreview && totalScore > 0;
	const tone = (points) => (isPreview ? " preview" : points ? "" : " muted");
	// an older format declares no maxCount, so a preview has nothing to put in the column
	const label = (points, suffix = "") => (isPreview && !points ? "" : points + suffix);

	/**
	 * @param {EventFormatScoringBreakdownRecord} record
	 * @param {number} groupIndex
	 */
	const pointsFor = (record, groupIndex) => {
		if (isPreview) {
			return maxPointsFor(record);
		}

		const scoreRecord = scoreBreakdown?.find(
			(s) =>
				s.Name === record.name &&
				(s.StageIndex === undefined || s.StageIndex === groupIndex)
		);
		return scoreRecord?.Score ?? 0;
	};

	/**
	 * @param {EventFormatScoringBreakdownGroup} scoreGroup
	 * @param {number} index
	 */
	const card = (scoreGroup, index) => {
		const rows = scoreGroup.records.map((record) => ({
			record,
			points: pointsFor(record, index),
		}));
		const subtotal = rows.reduce((sum, row) => sum + row.points, 0);

		return (
			<div className="score-card" key={index}>
				<div className="card-head">
					<span className="name">{scoreGroup.name}</span>
					<span className={"subtotal" + tone(subtotal)}>
						{label(subtotal, " pts")}
					</span>
				</div>
				{rows.map(({ record, points }, i) => (
					<div className="score-row" key={i}>
						<div className="row-label">
							<label>{record.name}</label>
							<span>
								{record.subtitle ?? `(+${record.pointsPer} per correct pick)`}
							</span>
						</div>
						<div className={"row-points" + tone(points)}>{label(points)}</div>
					</div>
				))}
			</div>
		);
	};

	return (
		<div className="score-breakdown">
			<div className="panel-head">
				<h2>Scoring Breakdown</h2>
				<ModalCloseButton onClose={() => ModalHelper.closeModal()} />
			</div>
			{scoreGroups.map(card)}
			{(!isPreview || maxPoints > 0) && (
				<div className={"score-total " + (hasTier ? league : "neutral")}>
					<div className="points">
						{isPreview ? maxPoints : totalScore}
						<span>pts</span>
					</div>
					{hasTier && (
						<div className="rank">
							{rankLabel(rank, percent)} · {league}
						</div>
					)}
					{isPreview && <div className="rank">Format maximum</div>}
				</div>
			)}
		</div>
	);
}
